import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

import { mockDriver } from "../../../../supabase/functions/_shared/messaging/driver.ts";
import { applyMergeFields } from "../../../../supabase/functions/_shared/templates.ts";
import { STUDIO_EMAIL } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { Contact, LibraryFile, MessageTemplate } from "../types";
import { attachLibraryFile } from "./libraryFiles";

export const MessageComposer = ({ contact }: { contact: Contact }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { data: templates = [] } = useGetList<MessageTemplate>(
    "message_templates",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const channel = "email" as const;
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendLater, setSendLater] = useState("");
  const [fileId, setFileId] = useState("");
  const [sending, setSending] = useState(false);
  const { data: files = [] } = useGetList<LibraryFile>("library_files", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const email = contact.email_jsonb?.find((item) => item.email)?.email;
  const blocked = contact.email_unsubscribed || contact.do_not_contact;
  const to = email;
  const channelTemplates = templates.filter(
    (template) => template.channel === "email",
  );

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((item) => String(item.id) === id);
    if (!template) return;
    const values = {
      first_name: contact.first_name,
      last_name: contact.last_name,
      rep_name: identity?.fullName ?? "",
      property_address: contact.property_address,
    };
    setBody(applyMergeFields(template.body, values));
    setSubject(
      template.subject ? applyMergeFields(template.subject, values) : "",
    );
  };

  const send = async () => {
    if (!to || !body.trim() || blocked) return;
    const scheduledAt = sendLater ? new Date(sendLater) : null;
    const isScheduled =
      scheduledAt != null &&
      !Number.isNaN(scheduledAt.getTime()) &&
      scheduledAt.getTime() > Date.now();
    setSending(true);
    try {
      const from = STUDIO_EMAIL;
      const now = new Date().toISOString();
      if (isScheduled && scheduledAt) {
        await dataProvider.create("messages", {
          data: {
            contact_id: contact.id,
            sales_id: identity?.id,
            channel,
            direction: "outbound",
            status: "scheduled",
            subject: subject || null,
            body: body.trim(),
            from_address: from,
            to_address: to,
            provider: "mock",
            scheduled_at: scheduledAt.toISOString(),
            file_id: fileId ? Number(fileId) : null,
            created_at: now,
          },
        });
        if (fileId) await attachLibraryFile(dataProvider, contact.id, fileId);
        notify("Message scheduled");
      } else {
        const result = await mockDriver.send({
          channel,
          to,
          from,
          subject,
          body: body.trim(),
        });
        await dataProvider.create("messages", {
          data: {
            contact_id: contact.id,
            sales_id: identity?.id,
            channel,
            direction: "outbound",
            status: result.status,
            subject: subject || null,
            body: body.trim(),
            from_address: from,
            to_address: to,
            provider: result.provider,
            provider_message_id: result.providerMessageId,
            sent_at: result.sentAt,
            delivered_at: result.deliveredAt ?? null,
            file_id: fileId ? Number(fileId) : null,
            created_at: now,
          },
        });
        if (fileId) await attachLibraryFile(dataProvider, contact.id, fileId);
        notify(
          result.status === "failed"
            ? result.error || "Message failed"
            : "Message saved",
          { type: result.status === "failed" ? "error" : "success" },
        );
      }
      setBody("");
      setSubject("");
      setTemplateId("");
      setSendLater("");
      setFileId("");
    } catch (error) {
      console.error(error);
      notify("Could not save the message", { type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 space-y-2">
      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Template"
          className="h-8 rounded-md border bg-transparent px-2 text-sm"
          value={templateId}
          onChange={(event) => applyTemplate(event.target.value)}
        >
          <option value="">Template</option>
          {channelTemplates.map((template) => (
            <option key={template.id} value={String(template.id)}>
              {template.name}
            </option>
          ))}
        </select>
      </div>
      {blocked ? (
        <p className="text-sm text-muted-foreground">
          Email is off for this person.
        </p>
      ) : null}
      <Input
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        placeholder="Subject"
        aria-label="Subject"
      />
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Email"
        aria-label="Email"
        rows={3}
      />
      <select
        aria-label="Include a file"
        className="h-8 max-w-xs rounded-md border bg-transparent px-2 text-sm"
        value={fileId}
        onChange={(event) => setFileId(event.target.value)}
      >
        <option value="">Include a file</option>
        {files.map((file) => (
          <option key={file.id} value={String(file.id)}>
            {file.name}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          type="datetime-local"
          value={sendLater}
          onChange={(event) => setSendLater(event.target.value)}
          aria-label="Send later"
          className="max-w-xs"
        />
        <Button
          type="button"
          size="sm"
          disabled={sending || blocked || !to || !body.trim()}
          onClick={() => void send()}
        >
          {sending ? "Saving…" : sendLater ? "Schedule" : "Send"}
        </Button>
      </div>
    </div>
  );
};
